import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Slider,
  FormControlLabel,
  Checkbox,
  Alert
} from '@mui/material';
import { createUpdate, updateVersion } from '../../services/deviceAppUpdateService';

export default function AddVersionModal({ open, onClose, onSuccess, editingVersion = null }) {
  const isEditMode = !!editingVersion;
  const hasDevices = editingVersion?.devicesOnVersion > 0;
  
  const [formData, setFormData] = useState({
    versionName: '',
    versionCode: '',
    apkUrl: '',
    sha256: '',
    releaseNotes: '',
    mandatory: false,
    rolloutPercent: 0
  });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Popola form quando entra in modalità editing
  React.useEffect(() => {
    if (editingVersion) {
      setFormData({
        versionName: editingVersion.versionName || '',
        versionCode: editingVersion.versionCode || '',
        apkUrl: editingVersion.apkUrl || '',
        sha256: editingVersion.sha256 || '',
        releaseNotes: editingVersion.releaseNotes || '',
        mandatory: editingVersion.mandatory || false,
        rolloutPercent: editingVersion.rolloutPercent || 0
      });
    } else {
      // Reset form quando non è in editing
      setFormData({
        versionName: '',
        versionCode: '',
        apkUrl: '',
        sha256: '',
        releaseNotes: '',
        mandatory: false,
        rolloutPercent: 0
      });
    }
  }, [editingVersion]);

  const handleChange = (field, value) => {
    const updates = { [field]: value };
    
    // Se cambia versionName, ricalcola automaticamente versionCode.
    // - Creazione: sempre auto
    // - Editing: auto SOLO se nessun dispositivo ha installato la versione
    // - Editing con dispositivi: versionCode fisso
    if (field === 'versionName' && (!isEditMode || !hasDevices)) {
      const versionCode = calculateVersionCode(value);
      if (versionCode !== null) {
        updates.versionCode = versionCode;
      }
    }
    
    setFormData(prev => ({ ...prev, ...updates }));
    setError(null);
  };

  // Calcola versionCode dalla versionName (stessa formula di build.gradle)
  const calculateVersionCode = (versionName) => {
    const parts = versionName.trim().split('.');
    if (parts.length < 1 || parts.length > 3) return null;
    
    const major = parseInt(parts[0]) || 0;
    const minor = parts.length > 1 ? (parseInt(parts[1]) || 0) : 0;
    const patch = parts.length > 2 ? (parseInt(parts[2]) || 0) : 0;
    
    // Formula: major*10000 + minor*100 + patch
    return major * 10000 + minor * 100 + patch;
  };

  const validateForm = () => {
    if (!formData.versionName.trim()) {
      setError('Il nome versione è obbligatorio');
      return false;
    }

    const versionCode = parseInt(formData.versionCode);
    if (isNaN(versionCode) || versionCode <= 0) {
      setError('Versione non valida - verifica il formato (es. 1.2.9)');
      return false;
    }

    if (!formData.apkUrl.trim() || !formData.apkUrl.startsWith('http')) {
      setError('L\'URL APK deve essere un URL HTTP/HTTPS valido');
      return false;
    }

    if (!/^[a-f0-9]{64}$/i.test(formData.sha256)) {
      setError('SHA-256 deve essere esattamente 64 caratteri esadecimali');
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);
      setError(null);

      if (isEditMode) {
        // Modalità editing - invia solo campi modificabili
        const updateData = {
          versionName: formData.versionName,
          releaseNotes: formData.releaseNotes,
          mandatory: formData.mandatory,
          minSupportedVersionCode: formData.minSupportedVersionCode
        };

        // versionCode può essere cambiato solo se nessun dispositivo ha installato la versione
        if (!hasDevices) {
          const newVersionCode = parseInt(formData.versionCode);
          const originalVersionCode = parseInt(editingVersion.versionCode);
          if (!isNaN(newVersionCode) && newVersionCode !== originalVersionCode) {
            updateData.versionCode = newVersionCode;
          }
        }
        
        // apkUrl è sempre modificabile (utile per migrazione storage)
        updateData.apkUrl = formData.apkUrl;
        
        // sha256 può essere modificato solo se non ci sono dispositivi
        if (!hasDevices) {
          updateData.sha256 = formData.sha256;
        }
        
        await updateVersion(editingVersion.versionCode, updateData);
      } else {
        // Modalità creazione
        await createUpdate({
          ...formData,
          versionCode: parseInt(formData.versionCode),
          rolloutPercent: formData.rolloutPercent
        });
      }

      onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{isEditMode ? `Modifica Versione ${editingVersion?.versionName}` : 'Aggiungi Nuova Versione App'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {!isEditMode && (
            <Alert severity="info">
              💡 In creazione, il Codice Versione viene calcolato automaticamente dal Nome Versione.
            </Alert>
          )}
          {isEditMode && hasDevices && (
            <Alert severity="warning">
              ⚠️ Il campo SHA-256 non può essere modificato perché {editingVersion.devicesOnVersion} dispositivo{editingVersion.devicesOnVersion > 1 ? 'i' : ''} ha già installato questa versione. Anche il Codice Versione (versionCode) è fisso. L'URL APK può essere modificato (es. migrazione storage).
            </Alert>
          )}
          {isEditMode && !hasDevices && (
            <Alert severity="info">
              💡 Nessun dispositivo ha ancora installato questa versione. Puoi modificare tutti i campi. Il Codice Versione viene calcolato automaticamente dal Nome Versione.
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="Nome Versione"
              value={formData.versionName}
              onChange={(e) => handleChange('versionName', e.target.value)}
              placeholder="es. 1.2.9"
              required
              fullWidth
            />
            <TextField
              label={isEditMode ? 'Codice Versione' : 'Codice Versione (Auto)'}
              type="number"
              value={formData.versionCode}
              disabled
              fullWidth
              helperText={
                !isEditMode
                  ? 'Calcolato automaticamente'
                  : (hasDevices ? 'Fisso (dispositivi già aggiornati)' : 'Calcolato automaticamente dal Nome Versione')
              }
              sx={{
                '& .MuiInputBase-root': {
                  backgroundColor: '#f5f5f5'
                },
                '& .MuiInputBase-input.Mui-disabled': {
                  WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)',
                  color: 'rgba(0, 0, 0, 0.38)',
                  cursor: 'not-allowed'
                },
                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield'
                }
              }}
            />
          </Box>

          <TextField
            label="URL APK"
            value={formData.apkUrl}
            onChange={(e) => handleChange('apkUrl', e.target.value)}
            placeholder="https://example.com/soontip-v1.2.9.apk"
            required
            fullWidth
            helperText={isEditMode && hasDevices ? "Modificabile (es. migrazione storage)" : ""}
          />

          <TextField
            label="Hash SHA-256"
            value={formData.sha256}
            onChange={(e) => handleChange('sha256', e.target.value)}
            placeholder="64 caratteri esadecimali"
            required
            disabled={isEditMode && hasDevices}
            fullWidth
            helperText={isEditMode && hasDevices ? "Non modificabile (dispositivi già aggiornati)" : (isEditMode ? "Modificabile" : "Calcola con: shasum -a 256 app-release.apk")}
            sx={isEditMode && hasDevices ? {
              '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' },
              '& .MuiInputBase-input.Mui-disabled': { WebkitTextFillColor: 'rgba(0, 0, 0, 0.38)' }
            } : {}}
          />

          <TextField
            label="Note di Rilascio"
            value={formData.releaseNotes}
            onChange={(e) => handleChange('releaseNotes', e.target.value)}
            placeholder="Novità di questa versione..."
            multiline
            rows={3}
            fullWidth
          />

          <Box>
            <Typography gutterBottom>
              Distribuzione Iniziale: {formData.rolloutPercent}%
            </Typography>
            <Slider
              value={formData.rolloutPercent}
              onChange={(e, val) => handleChange('rolloutPercent', val)}
              marks={[
                { value: 0, label: '0%' },
                { value: 10, label: '10%' },
                { value: 50, label: '50%' },
                { value: 100, label: '100%' }
              ]}
              step={5}
              min={0}
              max={100}
            />
            <Typography variant="caption" color="text.secondary">
              Inizia con 0% per test canary, poi aumenta gradualmente.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={formData.mandatory}
                onChange={(e) => handleChange('mandatory', e.target.checked)}
              />
            }
            label="Aggiornamento Obbligatorio (blocca l'app finché non installato)"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Annulla
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? (isEditMode ? 'Salvataggio...' : 'Creazione...') : (isEditMode ? 'Salva Modifiche' : 'Crea Versione')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
